import { useFormContext } from 'react-hook-form';
import { motion } from 'framer-motion';
import { CreditCard, Zap, Users, Receipt, RotateCcw, Plus, Minus, Send } from 'lucide-react';
import { StripeNodeFormValues, StripeOperation } from '../../../types/stripe';
import { STRIPE_OPERATIONS, STRIPE_OPERATION_INFO } from '../../../data/stripe-constants';

interface Step1OperationProps {
	config: StripeNodeFormValues;
	onChange: (config: Partial<StripeNodeFormValues>) => void;
}

const OPERATION_ICONS: Record<StripeOperation, React.ComponentType<any>> = {
	create_payment_intent: CreditCard,
	confirm_payment_intent: CreditCard,
	refund_payment: RotateCcw,
	create_customer: Users,
	attach_payment_method: CreditCard,
	create_subscription: Zap,
	cancel_subscription: Minus,
	create_invoice: Receipt,
	finalize_invoice: Receipt,
	send_invoice: Send,
};

export function Step1Operation({ config, onChange }: Step1OperationProps) {
	const { register, watch, setValue } = useFormContext<StripeNodeFormValues>();
	const watchedOperation = watch('operation');

	const handleOperationChange = (operation: StripeOperation) => {
		onChange({ operation });
	};

	return (
		<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
			<div>
				<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
					Choose Stripe Operation
				</h3>
				<p className="text-sm text-gray-600 dark:text-gray-400">
					Select what this node will do in your workflow
				</p>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
				{STRIPE_OPERATIONS.map((operation) => {
					const Icon = OPERATION_ICONS[operation];
					const info = STRIPE_OPERATION_INFO[operation];
					const isSelected = watchedOperation === operation;

					return (
						<motion.div
							key={operation}
							whileHover={{ scale: 1.02 }}
							whileTap={{ scale: 0.98 }}
						>
							<label className="block">
								<input
									type="radio"
									value={operation}
									{...register('operation')}
									className="sr-only"
									onChange={(e) => handleOperationChange(e.target.value as StripeOperation)}
								/>
								<div
									className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
										isSelected
											? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
											: 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
									}`}
								>
									<div className="flex items-start space-x-3">
										<div
											className={`p-2 rounded-lg ${
												isSelected
													? 'bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-400'
													: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
											}`}
										>
											<Icon className="w-5 h-5" />
										</div>
										<div className="flex-1 min-w-0">
											<h4 className="text-sm font-medium text-gray-900 dark:text-white">
												{info.title}
											</h4>
											<p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
												{info.description}
											</p>
											{info.requiredFields.length > 0 && (
												<div className="mt-2">
													<span className="text-xs text-gray-500 dark:text-gray-500">
														Required: {info.requiredFields.join(', ')}
													</span>
												</div>
											)}
										</div>
									</div>
								</div>
							</label>
						</motion.div>
					);
				})}
			</div>

			{watchedOperation && (
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
				>
					<div className="flex items-start space-x-3">
						<div className="p-2 bg-blue-100 dark:bg-blue-800 rounded-lg">
							<CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
						</div>
						<div>
							<h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
								{STRIPE_OPERATION_INFO[watchedOperation]?.title}
							</h4>
							<p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
								{STRIPE_OPERATION_INFO[watchedOperation]?.description}
							</p>
							{STRIPE_OPERATION_INFO[watchedOperation]?.webhookEvents.length > 0 && (
								<div className="mt-2">
									<span className="text-xs text-blue-600 dark:text-blue-400">
										Webhook events: {STRIPE_OPERATION_INFO[watchedOperation]?.webhookEvents.join(', ')}
									</span>
								</div>
							)}
						</div>
					</div>
				</motion.div>
			)}
		</div>
	);
}
